import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Activity, Users, TrendingUp, CalendarDays, Search, ChevronDown, ChevronUp,
  AlertTriangle, CheckCircle, Clock, BarChart3, User, FileText, X, ClipboardList,
  ToggleLeft, ToggleRight, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getScoreColor = (percentage) => {
  if (percentage <= 25) return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', bar: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
  if (percentage <= 50) return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', bar: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700 border-amber-200' };
  if (percentage <= 75) return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', bar: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700 border-orange-200' };
  return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', bar: 'bg-red-500', badge: 'bg-red-100 text-red-700 border-red-200' };
};

const getScoreLabel = (percentage) => {
  if (percentage <= 25) return 'Excelente';
  if (percentage <= 50) return 'Bueno';
  if (percentage <= 75) return 'Atención';
  return 'Crítico';
};

const AdminHealthDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [allTests, setAllTests] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [expandedTestId, setExpandedTestId] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, testsRes, tplRes] = await Promise.all([
        axios.get(`${API}/health-tests/admin/stats`),
        axios.get(`${API}/health-tests/all`),
        axios.get(`${API}/health-templates/admin/all`).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setAllTests(testsRes.data);
      setTemplates(tplRes.data || []);
    } catch (error) {
      console.error('Error fetching health test data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTemplate = async (tplId) => {
    try {
      const res = await axios.put(`${API}/health-templates/${tplId}/toggle`);
      setTemplates(prev => prev.map(t => t.id === tplId ? { ...t, active: res.data.active } : t));
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Error al cambiar estado');
    }
  };

  const filteredClients = useMemo(() => {
    if (!stats?.client_summaries) return [];
    if (!searchQuery.trim()) return stats.client_summaries;
    const q = searchQuery.toLowerCase();
    return stats.client_summaries.filter(c =>
      c.user_name.toLowerCase().includes(q)
    );
  }, [stats, searchQuery]);

  const selectedClientTests = useMemo(() => {
    if (!selectedClientId) return [];
    return allTests.filter(t => t.user_id === selectedClientId);
  }, [selectedClientId, allTests]);

  const selectedClientInfo = useMemo(() => {
    if (!selectedClientId || !stats?.client_summaries) return null;
    return stats.client_summaries.find(c => c.user_id === selectedClientId);
  }, [selectedClientId, stats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="health-dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-health-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Test de Salud
          </h1>
          <p className="text-gray-500 mt-1">Panel general de encuestas de salud de todos los clientes</p>
        </div>
      </div>

      {/* Templates Dropdown Section */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="templates-section">
        <button
          onClick={() => setShowTemplates(!showTemplates)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50/50 transition-colors"
          data-testid="templates-toggle-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center shadow-lg">
              <ClipboardList size={20} className="text-white" />
            </div>
            <div className="text-left">
              <p className="font-bold text-sm text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Evaluaciones de Salud</p>
              <p className="text-xs text-gray-400">{templates.length} test{templates.length !== 1 ? 's' : ''} en el sistema | {templates.filter(t => t.active).length} activo{templates.filter(t => t.active).length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {showTemplates ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </button>
        {showTemplates && (
          <div className="px-4 pb-4 space-y-2 animate-fade-in border-t border-gray-50">
            {templates.map(tpl => (
              <div key={tpl.id} className={`flex items-center justify-between p-3 rounded-xl border transition-all ${tpl.active ? 'border-emerald-100 bg-emerald-50/50' : 'border-gray-100 bg-gray-50/50 opacity-60'}`} data-testid={`template-row-${tpl.slug}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${tpl.color}20` }}>
                    <Activity size={16} style={{ color: tpl.color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-gray-900 truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{tpl.name}</p>
                    <p className="text-[10px] text-gray-400">{tpl.total_questions} preguntas {tpl.blocks?.length > 0 ? `| ${tpl.blocks.length} bloques` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tpl.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'}`}>
                    {tpl.active ? 'Activo' : 'Inactivo'}
                  </span>
                  <button onClick={() => toggleTemplate(tpl.id)} className="p-1.5 rounded-lg hover:bg-white/80 transition-colors" data-testid={`toggle-${tpl.slug}`}>
                    {tpl.active ? <ToggleRight size={22} className="text-emerald-500" /> : <ToggleLeft size={22} className="text-gray-400" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="health-kpi-cards">
        <KPICard
          icon={FileText}
          label="Total Encuestas"
          value={stats?.total_tests || 0}
          color="emerald"
        />
        <KPICard
          icon={Users}
          label="Clientes Evaluados"
          value={stats?.unique_clients || 0}
          color="blue"
        />
        <KPICard
          icon={BarChart3}
          label="Promedio General"
          value={`${stats?.avg_percentage || 0}%`}
          color="amber"
        />
        <KPICard
          icon={CalendarDays}
          label="Este Mes"
          value={stats?.tests_this_month || 0}
          color="violet"
        />
      </div>

      {/* Interpretation Guide */}
      <div className="grid grid-cols-4 gap-2" data-testid="score-guide">
        {[
          { range: '0-25%', label: 'Excelente', color: 'bg-emerald-500' },
          { range: '26-50%', label: 'Bueno', color: 'bg-amber-500' },
          { range: '51-75%', label: 'Atención', color: 'bg-orange-500' },
          { range: '76-100%', label: 'Crítico', color: 'bg-red-500' },
        ].map((g) => (
          <div key={g.range} className="flex items-center gap-2 text-xs text-gray-600">
            <div className={`w-2.5 h-2.5 rounded-full ${g.color} flex-shrink-0`} />
            <span className="font-medium">{g.range}</span>
            <span className="hidden sm:inline text-gray-400">— {g.label}</span>
          </div>
        ))}
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Client List Panel */}
        <div className={`${selectedClientId ? 'lg:col-span-2' : 'lg:col-span-5'}`}>
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-gray-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Clientes con Encuestas
                </CardTitle>
                <Badge variant="outline" className="text-xs">{filteredClients.length} clientes</Badge>
              </div>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  placeholder="Buscar cliente por nombre..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-gray-50 border-gray-200 focus:border-[#467322] focus:ring-[#467322]/20 rounded-xl h-10"
                  data-testid="search-client-input"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X size={14} />
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {filteredClients.length === 0 ? (
                <div className="py-12 text-center text-gray-400">
                  <Activity size={36} className="mx-auto mb-3 opacity-40" />
                  <p className="text-sm">{searchQuery ? 'No se encontraron clientes' : 'No hay encuestas registradas'}</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                  {filteredClients.map((client) => {
                    const color = getScoreColor(client.last_percentage);
                    const isSelected = selectedClientId === client.user_id;
                    return (
                      <button
                        key={client.user_id}
                        onClick={() => {
                          setSelectedClientId(isSelected ? null : client.user_id);
                          setExpandedTestId(null);
                        }}
                        className={`w-full text-left p-3 rounded-xl border transition-all duration-200 ${
                          isSelected
                            ? 'border-[#467322] bg-[#467322]/5 shadow-sm'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                        }`}
                        data-testid={`client-row-${client.user_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#467322] text-white' : 'bg-gray-100 text-gray-500'}`}>
                              <User size={16} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-sm text-gray-800 truncate">{client.user_name}</p>
                              <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                                <span>{client.test_count} encuesta{client.test_count > 1 ? 's' : ''}</span>
                                <span>·</span>
                                <span>{new Date(client.last_test_date).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge className={`${color.badge} text-xs border`}>
                              {client.last_percentage}%
                            </Badge>
                            {isSelected ? <ChevronUp size={14} className="text-[#467322]" /> : <ChevronDown size={14} className="text-gray-300" />}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail Panel - Only visible when a client is selected */}
        {selectedClientId && selectedClientInfo && (
          <div className="lg:col-span-3 space-y-4" data-testid="client-detail-panel">
            {/* Client Header */}
            <Card className="border border-[#467322]/20 bg-gradient-to-r from-[#467322] to-[#3A601C] text-white shadow-lg">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      <User size={24} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        {selectedClientInfo.user_name}
                      </h2>
                      <p className="text-white/70 text-sm">
                        {selectedClientInfo.test_count} encuesta{selectedClientInfo.test_count > 1 ? 's' : ''} realizadas · Promedio: {selectedClientInfo.avg_percentage}%
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => { setSelectedClientId(null); setExpandedTestId(null); }}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                    data-testid="close-detail-btn"
                  >
                    <X size={20} />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Tests History */}
            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-gray-800 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <Clock size={18} className="text-[#467322]" />
                  Historial de Encuestas
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {selectedClientTests.length === 0 ? (
                  <p className="text-center text-gray-400 py-6 text-sm">Sin encuestas registradas</p>
                ) : (
                  selectedClientTests.map((test, idx) => {
                    const color = getScoreColor(test.percentage);
                    const label = getScoreLabel(test.percentage);
                    const isExpanded = expandedTestId === test.id;
                    const sectionScores = test.scores || {};
                    const sortedSections = Object.entries(sectionScores)
                      .filter(([name]) => name !== 'Comentarios Adicionales')
                      .sort((a, b) => (b[1]?.percentage || b[1] || 0) - (a[1]?.percentage || a[1] || 0));

                    return (
                      <div key={test.id} className={`border rounded-xl overflow-hidden transition-all duration-200 ${isExpanded ? 'border-[#467322]/30 shadow-sm' : 'border-gray-100'}`}>
                        {/* Test Row */}
                        <button
                          onClick={() => setExpandedTestId(isExpanded ? null : test.id)}
                          className={`w-full text-left p-4 flex items-center justify-between transition-colors ${isExpanded ? 'bg-[#f8faf6]' : 'hover:bg-gray-50'}`}
                          data-testid={`test-row-${test.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${color.bg} ${color.text}`}>
                              #{idx + 1}
                            </div>
                            <div>
                              <p className="font-semibold text-sm text-gray-800">
                                Test de Salud Física
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {new Date(test.completed_at).toLocaleDateString('es-DO', { day: '2-digit', month: 'long', year: 'numeric' })}
                                {' · '}
                                {new Date(test.completed_at).toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className={`text-lg font-bold ${color.text}`}>{test.percentage}%</p>
                              <p className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</p>
                            </div>
                            {isExpanded ? <ChevronUp size={16} className="text-[#467322]" /> : <ChevronDown size={16} className="text-gray-300" />}
                          </div>
                        </button>

                        {/* Expanded Detail */}
                        {isExpanded && (
                          <div className="border-t border-gray-100 p-4 bg-white space-y-4" data-testid={`test-detail-${test.id}`}>
                            {/* Overall Score Bar */}
                            <div className={`p-3 rounded-lg ${color.bg} ${color.border} border`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className={`text-xs font-semibold ${color.text}`}>Puntuación Total</span>
                                <span className={`text-sm font-bold ${color.text}`}>{test.total_score} / {test.max_score}</span>
                              </div>
                              <Progress value={test.percentage} className="h-2" />
                            </div>

                            {/* Section Scores */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Detalle por Sección</p>
                              <div className="space-y-2">
                                {sortedSections.map(([sectionName, data]) => {
                                  const pct = typeof data === 'object' ? (data.percentage || 0) : 0;
                                  const score = typeof data === 'object' ? (data.score || 0) : data;
                                  const sColor = getScoreColor(pct);
                                  return (
                                    <div key={sectionName} className="flex items-center gap-3">
                                      <span className="text-xs text-gray-600 w-32 flex-shrink-0 truncate" title={sectionName}>{sectionName}</span>
                                      <div className="flex-1 relative">
                                        <div className="h-5 bg-gray-100 rounded-full overflow-hidden">
                                          <div
                                            className={`h-full rounded-full transition-all duration-500 ${sColor.bar}`}
                                            style={{ width: `${Math.max(pct, 2)}%` }}
                                          />
                                        </div>
                                      </div>
                                      <span className={`text-xs font-bold w-10 text-right ${sColor.text}`}>{pct}%</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Areas of Concern */}
                            {sortedSections.filter(([_, data]) => {
                              const pct = typeof data === 'object' ? (data.percentage || 0) : 0;
                              return pct > 50;
                            }).length > 0 && (
                              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-xs font-semibold text-amber-700 flex items-center gap-1.5 mb-2">
                                  <AlertTriangle size={13} />
                                  Áreas que Requieren Atención
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                  {sortedSections
                                    .filter(([_, data]) => {
                                      const pct = typeof data === 'object' ? (data.percentage || 0) : 0;
                                      return pct > 50;
                                    })
                                    .map(([name, data]) => {
                                      const pct = typeof data === 'object' ? (data.percentage || 0) : 0;
                                      return (
                                        <Badge key={name} className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                                          {name}: {pct}%
                                        </Badge>
                                      );
                                    })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// KPI Card component
const KPICard = ({ icon: Icon, label, value, color }) => {
  const colorMap = {
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-100' },
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-100' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-100' },
    violet: { bg: 'bg-violet-50', icon: 'text-violet-600', border: 'border-violet-100' },
  };
  const c = colorMap[color] || colorMap.emerald;

  return (
    <Card className={`border ${c.border} shadow-none`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon size={20} className={c.icon} />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminHealthDashboardPage;
